import { Component, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { ScheduleSubmissionsService } from 'src/app/services/api/schedule-submissions/schedule-submissions.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { UserManagementService } from 'src/app/services/api/user-management/user-management.service';
import { ScheduleSubmissionsModel } from 'src/app/services/models/schedule/schedule-submissions.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { UserManagementOption } from 'src/app/services/models/user-management/user-management.model';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

interface ReportSubmissionRow {
  id: number;
  sequence?: number | string | null;
  academicTerm: string;
  year: string;
  teacherName: string;
  departmentName: string;
  updatedAt: string;
}

interface ReportSubmissionDataSource {
  data: ReportSubmissionRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-teach-compensation-report',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './teach-compensation-report.component.html',
  styleUrl: './teach-compensation-report.component.scss',
})
export class TeachCompensationReportComponent implements OnInit {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;

  form!: FormGroup;
  columns: any[] = [];
  datasource = signal<ReportSubmissionDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  pageSizeOptions = [10, 20, 50];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  departmentOptions: UserManagementOption[] = [];

  private currentPage = 1;
  private currentLimit = 50;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    academicTerm: 'academic_term_name',
    year: 'academic_term_year',
    teacherName: 'full_name',
    departmentName: 'department',
    updatedAt: 'updated_at',
  };
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthService);
  private scheduleSubmissionsService = inject(ScheduleSubmissionsService);
  private studyPeriodService = inject(StudyPeriodService);
  private userManagementService = inject(UserManagementService);
  private academicTerms: AcademicTermYearModel[] = [];

  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.listenFilters();
    this.loadFilterOptions();
  }

  onSearch(): void {
    this.loadSubmissions(1);
  }

  onViewRequests(row: ReportSubmissionRow): void {
    void this.router.navigate([
      '/teach-compensation-management/teach-compensation-report',
      row.id,
    ]);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadSubmissions(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadSubmissions(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadSubmissions(1);
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
      keyword: [''],
      departmentId: [null],
    });
  }

  private listenFilters(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadSubmissions(1));
  }

  private loadFilterOptions(): void {
    forkJoin({
      academicTerms: this.studyPeriodService.getAcademicTermYears().pipe(take(1)),
      departments: this.userManagementService.getDepartments(false).pipe(take(1)),
    }).subscribe({
      next: ({ academicTerms, departments }) => {
        this.academicTerms = academicTerms;
        const academicTermOptions = this.buildAcademicTermOptions(academicTerms);
        const yearOptions = this.buildAcademicYearOptions(academicTerms);

        this.academicTermOptions = academicTermOptions.length > 0 ? academicTermOptions : this.academicTermOptions;
        this.yearOptions = yearOptions.length > 0 ? yearOptions : this.yearOptions;
        this.departmentOptions = departments;
        this.ensureAcademicTermFilterValues();
        this.loadSubmissions(1);
      },
      error: (err) => {
        console.error(err);
        this.loadSubmissions(1);
      },
    });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
      },
      {
        prop: 'academicTerm',
        name: 'เทอมการศึกษา',
        sortable: true,
        width: 130,
        minWidth: 120,
      },
      {
        prop: 'year',
        name: 'ปีการศึกษา',
        sortable: true,
        width: 130,
        minWidth: 120,
      },
      {
        prop: 'teacherName',
        name: 'ชื่อ - นามสกุล',
        sortable: true,
        width: 260,
        minWidth: 220,
      },
      {
        prop: 'departmentName',
        name: 'สาขาวิชา',
        sortable: true,
        width: 220,
        minWidth: 180,
      },
      {
        prop: 'updatedAt',
        name: 'วันที่อัปเดตสถานะ',
        sortable: true,
        width: 190,
        minWidth: 170,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        cellClass: 'text-center teach-compensation-report-action-cell',
        headerClass: 'text-center teach-compensation-report-action-header',
      },
    ];
  }

  private loadSubmissions(page = this.currentPage): void {
    this.currentPage = page;
    this.tableLoading.set(true);

    this.scheduleSubmissionsService.getScheduleSubmissionsList({
      ...this.buildFilterParams(),
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        const rows = response.data.map((item) => this.mapSubmissionToRow(item));

        this.datasource.set(this.createDataSource(
          rows,
          response.page,
          response.limit,
          response.total,
          response.total_pages,
          (response.page - 1) * response.limit,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private buildFilterParams() {
    const { academicTerm, year, keyword, departmentId } = this.form.getRawValue();
    const selectedTerm = this.findAcademicTerm(academicTerm, year);
    const searchKeyword = String(keyword ?? '').trim();

    return {
      academic_term_id: selectedTerm?.id ?? null,
      keywords: searchKeyword !== '' ? searchKeyword : null,
      department_id: departmentId ?? null,
      approval_review: false,
      makeup_teaching_report: true,
    };
  }

  private mapSubmissionToRow(item: ScheduleSubmissionsModel): ReportSubmissionRow {
    return {
      id: item.id,
      sequence: item.sequence,
      academicTerm: item.academic_term.name,
      year: String(this.toBuddhistYear(item.academic_term.year)),
      teacherName: this.formatTeacherName(item),
      departmentName: this.getDepartmentName(item),
      updatedAt: this.formatDateTime(item.updated_at),
    };
  }

  private createDataSource(
    rows: ReportSubmissionRow[],
    page = 1,
    limit = 50,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
    offset = (page - 1) * limit,
  ): ReportSubmissionDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: normalizedPage === page ? offset : (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private formatTeacherName(item: ScheduleSubmissionsModel): string {
    const fullName = item.teacher.full_name.trim();
    const academicRank = String(item.teacher.academic_rank ?? '').trim();

    if (fullName === '') {
      return '-';
    }

    return academicRank === '' ? fullName : `${academicRank} ${fullName}`;
  }

  private getDepartmentName(item: ScheduleSubmissionsModel): string {
    const department = item.teacher.department;

    return department.name_th ||
      department.name ||
      department.name_en ||
      '-';
  }

  private formatDateTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = this.toBuddhistYear(date.getFullYear());
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === String(academicTermName) &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(this.toBuddhistYear(term.year)))
        .filter(Boolean),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private ensureAcademicTermFilterValues(): void {
    const current = this.form.getRawValue();
    const sessionTerm = this.authService.currentUser()?.academic_term;
    const sessionYear = sessionTerm?.year != null ? String(this.toBuddhistYear(Number(sessionTerm.year))) : null;
    const sessionName = sessionTerm?.name != null ? String(sessionTerm.name) : null;

    const nextAcademicTerm = this.academicTermOptions.includes(sessionName ?? '')
      ? sessionName
      : this.academicTermOptions.includes(current.academicTerm)
        ? current.academicTerm
        : this.academicTermOptions[0] ?? current.academicTerm;
    const nextYear = this.yearOptions.includes(sessionYear ?? '')
      ? sessionYear
      : this.yearOptions.includes(current.year)
        ? current.year
        : this.yearOptions[0] ?? current.year;

    this.form.patchValue({
      academicTerm: nextAcademicTerm,
      year: nextYear,
    }, { emitEvent: false });
  }

  private getDefaultAcademicTermName(): string {
    return String(this.authService.currentUser()?.academic_term?.name ?? '1');
  }

  private getDefaultBuddhistYear(): string {
    const year = Number(this.authService.currentUser()?.academic_term?.year ?? new Date().getFullYear());
    return String(this.toBuddhistYear(year));
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }
}
